import { useCallback } from "react";
import React from "react";

import { Rename } from "components/Prompts";
import { useDealParamsHooks, useModals } from "hooks";
import { useDealRoleGuard, useIsViewerPermission, usePrompt } from "hooks";
import { useDealActions, useDocumentsActions, useTaskManageActions, useViewActions } from "store";
import { IItemToShare } from "store/types/deal/share";

import { EntityType, RoleName } from "../../../constants";

export interface UseDocumentHookParams {
  document: {
    id: number;
    title: string;
    deal_id: number;
    is_private: boolean;
  };
  refetchHandler?: () => Promise<void>;
}

export const useDocumentHook = ({ document, refetchHandler }: UseDocumentHookParams) => {
  const { updateDocumentAction, deleteDocumentAction } = useDocumentsActions();
  const { patchFolderMetaDetailAction } = useDealActions();
  const { setCreateAction } = useTaskManageActions();
  const { setShareDialog } = useViewActions();
  const { deleteModal } = useModals();

  const { dealId } = useDealParamsHooks();
  const openPrompt = usePrompt();

  const isAnalystDealsViewerPermission = useIsViewerPermission("deals");

  const handleShare = useCallback(() => {
    setShareDialog({
      elements: [document.id],
      type: "document_id",
      shareName: document.title,
      dealId: dealId ?? null,
      itemToShare: document as unknown as IItemToShare,
      globalDoc: !dealId,
      cb: () => {
        refetchHandler?.() ?? Promise.resolve();
      }
    });
  }, [setShareDialog, document, dealId, refetchHandler]);

  const onConvertToGeneral = useCallback(async () => {
    await updateDocumentAction({ id: document.id, make_global: true });
    await refetchHandler?.();
  }, [document, updateDocumentAction]);

  const handleAddTask = useCallback(() => {
    setCreateAction({
      isOpen: true,
      entity_id: document.id,
      entity_type: "document"
    });
  }, [setCreateAction, document.id]);

  const handleDelete = useCallback(
    async (id: number) => {
      if (!document.id) return;
      await deleteDocumentAction(id);
      patchFolderMetaDetailAction({
        key: "delete",
        id: document.id,
        entityType: EntityType.DOCUMENT
      });
    },
    [deleteDocumentAction, document.id, patchFolderMetaDetailAction]
  );

  const handleDeleteDocument = useCallback(
    (id: number) => {
      if (!document.id) return;
      return deleteModal({
        element: "Delete item permanently?",
        message: "This cannot be undone",
        onPressSuccess: "Delete permanently",
        action: async () => await handleDelete(id)
      });
    },
    [document.id, deleteModal, handleDelete]
  );

  const handleChangePrivacy = useCallback(
    async (is_private: boolean) => {
      await updateDocumentAction({ id: document.id, is_private });
      patchFolderMetaDetailAction({
        key: "patch",
        id: document.id,
        entityType: EntityType.DOCUMENT,
        patch: { is_private }
      });
    },
    [document.id, updateDocumentAction, patchFolderMetaDetailAction]
  );

  const handleRename = useCallback(
    async (menuTriggerRef: React.RefObject<HTMLButtonElement>, onMenuClose: () => void) => {
      if (!document.id) return;
      const trigger = menuTriggerRef.current;
      if (!trigger) return;

      const triggerRect = trigger.getBoundingClientRect();
      const anchorPosition = {
        top: Math.round(triggerRect.bottom),
        left: Math.round(triggerRect.left)
      };

      onMenuClose();

      const title = await openPrompt({
        Component: Rename,
        componentType: "popover",
        anchorPosition,
        anchorOrigin: { vertical: "bottom", horizontal: "left" },
        transformOrigin: { vertical: "top", horizontal: "left" },
        props: { title: document.title }
      });
      if (title) {
        await updateDocumentAction({ id: document.id, title });
        patchFolderMetaDetailAction({
          key: "patch",
          id: document.id,
          entityType: EntityType.DOCUMENT,
          patch: { title }
        });
      }
    },
    [document.id, document.title, openPrompt, updateDocumentAction, patchFolderMetaDetailAction]
  );
  const isAvailableRoleWithPermissions = isAnalystDealsViewerPermission
    ? [RoleName.Admin]
    : [RoleName.Admin, RoleName.Analyst];

  const handleShareWithRole = useDealRoleGuard(handleShare, isAvailableRoleWithPermissions);
  const handleAddTaskWithRole = useDealRoleGuard(handleAddTask, isAvailableRoleWithPermissions);
  const onDeleteDocumentWithRole = useDealRoleGuard(handleDeleteDocument, isAvailableRoleWithPermissions);
  const handleChangePrivacyWithRole = useDealRoleGuard(handleChangePrivacy, isAvailableRoleWithPermissions);
  const handleRenameWithRole = useDealRoleGuard(handleRename, isAvailableRoleWithPermissions);
  const onConvertToGeneralWithRole = useDealRoleGuard(onConvertToGeneral, isAvailableRoleWithPermissions);

  return {
    handleShare: handleShareWithRole,
    handleAddTask: handleAddTaskWithRole,
    handleDeleteDocument: onDeleteDocumentWithRole,
    handleChangePrivacy: handleChangePrivacyWithRole,
    handleRename: handleRenameWithRole,
    handleConvertToGeneral: onConvertToGeneralWithRole
  };
};
